import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { plansAPI, employeesAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';

const BLANK_TASK = {
  task_name: '', description: '', assigned_to: '',
  duration_days: 1, effort_days: '', allocation_percent: 100, priority: 'Medium',
  earliest_start: '', constraint_reason: '',
};

const PlanDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { can } = useAuth();

  const [plan, setPlan] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cycle, setCycle] = useState(null);
  const [busy, setBusy] = useState(false);

  const [taskModal, setTaskModal] = useState(null);   // {mode:'add'|'edit', data}
  const [depModal, setDepModal] = useState(null);     // {successor_id}
  const [depForm, setDepForm] = useState({ predecessor_id: '', lag_days: 0 });

  const locked = plan?.status === 'Created';

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [p, e] = await Promise.allSettled([
        plansAPI.getById(id),
        employeesAPI.getAll(),
      ]);
      if (p.status === 'fulfilled') setPlan(p.value.data);
      if (e.status === 'fulfilled') setEmployees(e.value.data);
    } catch (err) {
      console.error('Error loading plan:', err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const refresh = async (result) => {
    if (result?.data?.cycle) setCycle(result.data.cycle);
    else setCycle(null);
    await load();
  };

  // ── Task actions ──────────────────────────────────────────────────────────
  const saveTask = async (e) => {
    e.preventDefault();
    if (busy) return;
    const d = taskModal.data;
    if (!d.task_name.trim()) return;
    try {
      setBusy(true);
      const payload = {
        ...d,
        assigned_to: d.assigned_to || null,
        duration_days: Math.max(1, parseInt(d.duration_days) || 1),
        effort_days: d.effort_days === '' ? null : d.effort_days,
        allocation_percent: parseInt(d.allocation_percent) || 100,
      };
      const res = taskModal.mode === 'add'
        ? await plansAPI.addTask(id, payload)
        : await plansAPI.updateTask(id, d.plan_task_id, payload);
      setTaskModal(null);
      await refresh(res);
    } catch (err) {
      alert(err.response?.data?.error || 'Could not save the task');
    } finally {
      setBusy(false);
    }
  };

  const removeTask = async (t) => {
    if (!window.confirm(`Remove "${t.task_name}" from the plan?`)) return;
    try {
      const res = await plansAPI.deleteTask(id, t.plan_task_id);
      await refresh(res);
    } catch (err) {
      alert('Could not remove the task');
    }
  };

  // ── Dependency actions ────────────────────────────────────────────────────
  const addDependency = async (e) => {
    e.preventDefault();
    if (!depForm.predecessor_id) return;
    try {
      setBusy(true);
      const res = await plansAPI.addDependency(id, {
        predecessor_id: Number(depForm.predecessor_id),
        successor_id: depModal.successor_id,
        lag_days: parseInt(depForm.lag_days) || 0,
      });
      setDepModal(null);
      setDepForm({ predecessor_id: '', lag_days: 0 });
      await refresh(res);
    } catch (err) {
      const msg = err.response?.data?.error || 'Could not add the dependency';
      const c = err.response?.data?.cycle;
      alert(c ? `${msg}\n\nInvolved: ${c.join(' → ')}` : msg);
    } finally {
      setBusy(false);
    }
  };

  const removeDependency = async (depId) => {
    try {
      const res = await plansAPI.deleteDependency(id, depId);
      await refresh(res);
    } catch (err) {
      alert('Could not remove the dependency');
    }
  };

  // ── Materialise ───────────────────────────────────────────────────────────
  const createProject = async () => {
    if (!window.confirm(
      `Create a real project from this plan?\n\n` +
      `${plan.tasks.length} tasks will be created with their scheduled dates. ` +
      `Planned dates are locked after creation.`
    )) return;
    try {
      setBusy(true);
      const res = await plansAPI.createProject(id);
      alert(`Project created with ${res.data.task_count} tasks.`);
      navigate(`/projects/${res.data.project_id}`);
    } catch (err) {
      const msg = err.response?.data?.error || 'Could not create the project';
      const c = err.response?.data?.cycle;
      alert(c ? `${msg}\n\nInvolved: ${c.join(' → ')}` : msg);
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <div className="loading">Loading plan…</div>;
  if (!plan) return <div className="card"><p>Plan not found.</p></div>;

  const depsFor = (taskId) => plan.dependencies.filter(d => d.successor_id === taskId);
  const taskName = (tid) => plan.tasks.find(t => t.plan_task_id === tid)?.task_name || '?';

  // Tasks eligible as predecessors (exclude self and existing predecessors)
  const eligiblePreds = (successorId) => {
    const existing = depsFor(successorId).map(d => d.predecessor_id);
    return plan.tasks.filter(t =>
      t.plan_task_id !== successorId && !existing.includes(t.plan_task_id)
    );
  };

  const planEnd = plan.tasks.reduce((max, t) =>
    t.computed_end && (!max || t.computed_end > max) ? t.computed_end : max, null);

  return (
    <div>
      <div className="page-header">
        <div>
          <Link to="/plans">← All plans</Link>
          <h2 className="page-title" style={{ marginTop: 8 }}>{plan.plan_name}</h2>
          <p className="page-subtitle">
            {plan.customer ? `${plan.customer} · ` : ''}
            Starts {plan.start_date}
            {planEnd ? ` · Ends ${planEnd}` : ''}
            {` · ${plan.tasks.length} task${plan.tasks.length === 1 ? '' : 's'}`}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          {!locked && can('createEditProject') && (
            <>
              <button className="btn btn-secondary"
                onClick={() => { setTaskModal({ mode: 'add', data: { ...BLANK_TASK } }); }}>
                ➕ Add Task
              </button>
              <button className="btn btn-success" onClick={createProject}
                disabled={busy || plan.tasks.length === 0 || !!cycle}>
                🚀 Create Project
              </button>
            </>
          )}
          {locked && plan.created_project_id && (
            <Link to={`/projects/${plan.created_project_id}`} className="btn btn-primary">
              View Project →
            </Link>
          )}
        </div>
      </div>

      {locked && (
        <div className="card" style={{ borderLeft: '4px solid #2ecc71' }}>
          <strong>✓ This plan has been created as a project.</strong>
          <p style={{ marginTop: 6, color: '#7f8c8d' }}>
            It is now read-only. Manage the work from the project itself.
          </p>
        </div>
      )}

      {cycle && (
        <div className="card" style={{ borderLeft: '4px solid #e74c3c' }}>
          <strong style={{ color: '#c0392b' }}>⚠ Circular dependency detected</strong>
          <p style={{ marginTop: 6 }}>
            These tasks depend on each other in a loop and cannot be scheduled:{' '}
            <strong>{cycle.join(' → ')}</strong>. Remove a dependency to fix it.
          </p>
        </div>
      )}

      <div className="card">
        <div className="card-header">
          <span className="card-title">Plan Tasks</span>
          <span className="page-subtitle">
            Dates are calculated from durations and dependencies — weekends skipped
          </span>
        </div>

        {plan.tasks.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">📋</div>
            <div className="empty-state-text">No tasks in this plan</div>
            <p>Add tasks, then link them with dependencies to build the schedule.</p>
          </div>
        ) : (
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th style={{ width: 40 }}>#</th>
                  <th>Task</th>
                  <th>Assigned To</th>
                  <th style={{ textAlign: 'center' }}>Duration</th>
                  <th>Depends On</th>
                  <th>Scheduled</th>
                  {!locked && <th>Actions</th>}
                </tr>
              </thead>
              <tbody>
                {plan.tasks.map((t, i) => {
                  const deps = depsFor(t.plan_task_id);
                  return (
                    <tr key={t.plan_task_id}>
                      <td>{i + 1}</td>
                      <td>
                        <strong>{t.task_name}</strong>
                        {t.description && (
                          <div style={{ fontSize: 12, color: '#7f8c8d', marginTop: 4 }}>
                            {t.description}
                          </div>
                        )}
                        <div style={{ marginTop: 4 }}>
                          <span className="badge badge-info">{t.priority}</span>
                          {t.constraint_reason && (
                            <span style={{ fontSize: 12, color: '#856404', marginLeft: 8 }}>
                              🔒 {t.constraint_reason}
                            </span>
                          )}
                        </div>
                      </td>
                      <td>{t.assigned_to_name || <span style={{ color: '#bdc3c7' }}>Unassigned</span>}</td>
                      <td style={{ textAlign: 'center' }}>
                        <strong>{t.duration_days}</strong>
                        <div style={{ fontSize: 11, color: '#7f8c8d' }}>
                          {t.duration_days === 1 ? 'day' : 'days'}
                        </div>
                      </td>
                      <td>
                        {deps.length === 0 && !t.earliest_start ? (
                          <span style={{ color: '#bdc3c7' }}>Starts immediately</span>
                        ) : (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                            {t.earliest_start && (
                              <span
                                title={t.constraint_reason || 'Cannot start before this date'}
                                style={{
                                  display: 'inline-flex', alignItems: 'center', gap: 6,
                                  background: '#fff3cd', color: '#856404', borderRadius: 12,
                                  padding: '3px 10px', fontSize: 12, fontWeight: 600,
                                }}>
                                🔒 Not before {t.earliest_start}
                              </span>
                            )}
                            {deps.map(d => (
                              <span key={d.dep_id} style={{
                                display: 'inline-flex', alignItems: 'center', gap: 6,
                                background: '#eef2f7', borderRadius: 12,
                                padding: '3px 10px', fontSize: 12, fontWeight: 600,
                              }}>
                                {taskName(d.predecessor_id)}
                                {d.lag_days !== 0 && (
                                  <em style={{ color: '#7f8c8d', fontWeight: 400 }}>
                                    {d.lag_days > 0 ? `+${d.lag_days}d` : `${d.lag_days}d`}
                                  </em>
                                )}
                                {!locked && (
                                  <button
                                    onClick={() => removeDependency(d.dep_id)}
                                    title="Remove dependency"
                                    style={{
                                      border: 'none', background: 'transparent',
                                      cursor: 'pointer', color: '#e74c3c', fontWeight: 700,
                                      padding: 0, lineHeight: 1,
                                    }}
                                  >×</button>
                                )}
                              </span>
                            ))}
                          </div>
                        )}
                        {!locked && eligiblePreds(t.plan_task_id).length > 0 && (
                          <button
                            className="btn btn-sm btn-secondary"
                            style={{ marginTop: 6 }}
                            onClick={() => {
                              setDepForm({ predecessor_id: '', lag_days: 0 });
                              setDepModal({ successor_id: t.plan_task_id, task_name: t.task_name });
                            }}
                          >+ Link</button>
                        )}
                      </td>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        {t.computed_start ? (
                          <>
                            <div><strong>{t.computed_start}</strong></div>
                            <div style={{ fontSize: 12, color: '#7f8c8d' }}>to {t.computed_end}</div>
                          </>
                        ) : <span style={{ color: '#bdc3c7' }}>—</span>}
                      </td>
                      {!locked && (
                        <td style={{ whiteSpace: 'nowrap' }}>
                          <button className="btn btn-sm btn-primary" style={{ marginRight: 8 }}
                            onClick={() => setTaskModal({ mode: 'edit', data: { ...t } })}>
                            Edit
                          </button>
                          <button className="btn btn-sm btn-danger" onClick={() => removeTask(t)}>
                            Delete
                          </button>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Task modal */}
      {taskModal && (
        <div className="modal-overlay" onClick={() => setTaskModal(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">
                {taskModal.mode === 'add' ? 'Add Plan Task' : 'Edit Plan Task'}
              </h3>
            </div>
            <form onSubmit={saveTask}>
              <div className="form-group">
                <label className="form-label">Task Name *</label>
                <input className="form-control" required value={taskModal.data.task_name}
                  onChange={e => setTaskModal(m => ({ ...m, data: { ...m.data, task_name: e.target.value } }))} />
              </div>
              <div className="form-group">
                <label className="form-label">Description</label>
                <textarea className="form-control" value={taskModal.data.description || ''}
                  onChange={e => setTaskModal(m => ({ ...m, data: { ...m.data, description: e.target.value } }))} />
              </div>
              <div className="grid grid-2">
                <div className="form-group">
                  <label className="form-label">Assigned To</label>
                  <select className="form-control" value={taskModal.data.assigned_to || ''}
                    onChange={e => setTaskModal(m => ({ ...m, data: { ...m.data, assigned_to: e.target.value } }))}>
                    <option value="">— Unassigned —</option>
                    {employees.map(emp => (
                      <option key={emp.employee_id} value={emp.employee_id}>{emp.employee_name}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Duration (working days) *</label>
                  <input type="number" min="1" className="form-control" required
                    value={taskModal.data.duration_days}
                    onChange={e => setTaskModal(m => ({ ...m, data: { ...m.data, duration_days: e.target.value } }))} />
                </div>
              </div>
              <div className="grid grid-2">
                <div className="form-group">
                  <label className="form-label">Allocation %</label>
                  <input type="number" min="0" max="100" className="form-control"
                    value={taskModal.data.allocation_percent}
                    onChange={e => setTaskModal(m => ({ ...m, data: { ...m.data, allocation_percent: e.target.value } }))} />
                </div>
                <div className="form-group">
                  <label className="form-label">Priority</label>
                  <select className="form-control" value={taskModal.data.priority}
                    onChange={e => setTaskModal(m => ({ ...m, data: { ...m.data, priority: e.target.value } }))}>
                    <option>Low</option><option>Medium</option><option>High</option><option>Critical</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-2">
                <div className="form-group">
                  <label className="form-label">Cannot start before</label>
                  <input type="date" className="form-control"
                    value={taskModal.data.earliest_start || ''}
                    onChange={e => setTaskModal(m => ({ ...m, data: { ...m.data, earliest_start: e.target.value } }))} />
                  <p style={{ fontSize: 12, color: '#7f8c8d', marginTop: 6 }}>
                    Optional. Use when a task is held up by resource availability,
                    a vendor, or anything outside the plan. Leave blank to start as
                    early as dependencies allow.
                  </p>
                </div>
                <div className="form-group">
                  <label className="form-label">Reason for the constraint</label>
                  <input className="form-control"
                    placeholder="e.g. Developer on leave until then"
                    value={taskModal.data.constraint_reason || ''}
                    onChange={e => setTaskModal(m => ({ ...m, data: { ...m.data, constraint_reason: e.target.value } }))} />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setTaskModal(null)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={busy}>
                  {busy ? 'Saving…' : 'Save Task'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Dependency picker */}
      {depModal && (
        <div className="modal-overlay" onClick={() => setDepModal(null)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 480 }}>
            <div className="modal-header">
              <h3 className="modal-title">Add Dependency</h3>
              <p className="page-subtitle" style={{ marginTop: 6 }}>
                <strong>{depModal.task_name}</strong> will start after the task you pick finishes.
              </p>
            </div>
            <form onSubmit={addDependency}>
              <div className="form-group">
                <label className="form-label">Must finish first *</label>
                <select className="form-control" required value={depForm.predecessor_id}
                  onChange={e => setDepForm(f => ({ ...f, predecessor_id: e.target.value }))}>
                  <option value="">— Select a task —</option>
                  {eligiblePreds(depModal.successor_id).map(t => (
                    <option key={t.plan_task_id} value={t.plan_task_id}>{t.task_name}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Lag (working days)</label>
                <input type="number" className="form-control" value={depForm.lag_days}
                  onChange={e => setDepForm(f => ({ ...f, lag_days: e.target.value }))} />
                <p style={{ fontSize: 12, color: '#7f8c8d', marginTop: 6 }}>
                  0 = starts the next working day. Positive adds a gap; negative overlaps.
                </p>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setDepModal(null)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={busy}>
                  {busy ? 'Linking…' : 'Add Link'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlanDetail;
